/* ============================================
   LAMIER - Premium Men's Grooming Brand Theme
   Application JavaScript
   ES6+ | No jQuery | Web Components
   ============================================ */

/* ==========================
   1. Utility Functions
   ========================== */

/**
 * Debounce - delays function execution until after wait ms
 * of silence since the last invocation.
 */
function debounce(fn, delay = 300) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Throttle - ensures function is called at most once
 * every wait ms.
 */
function throttle(fn, wait = 100) {
  let lastTime = 0;
  let timer;
  return function (...args) {
    const now = Date.now();
    const remaining = wait - (now - lastTime);
    clearTimeout(timer);
    if (remaining <= 0 || remaining > wait) {
      lastTime = now;
      fn.apply(this, args);
    } else {
      timer = setTimeout(() => {
        lastTime = Date.now();
        fn.apply(this, args);
      }, remaining);
    }
  };
}

/**
 * fetchJSON - Fetch wrapper with JSON parsing and error handling.
 */
async function fetchJSON(url, options = {}) {
  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || `HTTP ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`[Lamier] fetchJSON error: ${error.message}`, url);
    throw error;
  }
}

/**
 * formatMoney - Formats cents into a currency string.
 * Uses Shopify's money format if available, otherwise defaults.
 */
function formatMoney(cents, format = '') {
  if (typeof Shopify?.currency?.active !== 'undefined') {
    // Use Shopify's built-in formatting if available
    if (typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
  }

  // Fallback formatting
  const amount = (parseInt(cents, 10) / 100).toFixed(2);
  return `$${amount}`;
}

/**
 * getSectionInnerHTML - Parses an HTML string and returns
 * the innerHTML of the first element matching the selector.
 */
function getSectionInnerHTML(html, selector) {
  return new DOMParser()
    .parseFromString(html, 'text/html')
    .querySelector(selector)?.innerHTML || '';
}

/**
 * Trap focus inside an element (for accessibility on modals/drawers).
 */
function trapFocus(container) {
  const focusableElements = container.querySelectorAll(
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
  );
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  container.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable?.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable?.focus();
      }
    }
  });
}

/**
 * Remove focus trap from a container.
 */
function removeTrapFocus(container) {
  container.removeEventListener('keydown', trapFocus);
}

/* ==========================
   2. Intersection Observer Animations
   ========================== */

function initScrollAnimations() {
  const animatedElements = document.querySelectorAll(
    '.animate-on-scroll, [data-animate]'
  );

  if (!animatedElements.length) return;

  const observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  };

  const observer = new IntersectionObserver(
    throttle((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, 50),
    observerOptions
  );

  animatedElements.forEach((el) => observer.observe(el));
}

/* ==========================
   3. Custom Element: <lamier-header>
   ========================== */

class LamierHeader extends HTMLElement {
  constructor() {
    super();
    this.menuToggle = null;
    this.menuClose = null;
    this.mobileMenu = null;
    this.overlay = null;
    this.header = null;
    this.searchToggle = null;
    this.searchInput = null;
    this.menuItems = [];
  }

  connectedCallback() {
    // Cache DOM references
    this.menuToggle = this.querySelector('[data-menu-toggle]');
    this.menuClose = this.querySelector('[data-menu-close]');
    this.mobileMenu = this.querySelector('[data-mobile-menu]');
    this.overlay = this.querySelector('[data-menu-overlay]');
    this.header = this.querySelector('.header') || this.querySelector('header');
    this.searchToggle = this.querySelector('[data-search-toggle]');
    this.searchInput = this.querySelector('[data-search-input]');
    this.menuItems = Array.from(this.querySelectorAll('.header__menu-item'));

    // Bind event listeners
    this._onScroll = throttle(this._handleScroll.bind(this), 50);
    this._onEscape = this._handleEscape.bind(this);
    this._onResize = debounce(this._handleResize.bind(this), 150);

    this._bindEvents();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    document.removeEventListener('keydown', this._onEscape);
    window.removeEventListener('resize', this._onResize);
  }

  _bindEvents() {
    // Sticky header scroll behavior
    window.addEventListener('scroll', this._onScroll);
    this._handleScroll(); // Check initial scroll position

    // Mobile menu toggle
    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', () => this.openMenu());
    }

    if (this.menuClose) {
      this.menuClose.addEventListener('click', () => this.closeMenu());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeMenu());
    }

    // Escape key to close
    document.addEventListener('keydown', this._onEscape);

    // Search toggle
    if (this.searchToggle) {
      this.searchToggle.addEventListener('click', () => this._toggleSearch());
    }

    // Cart drawer toggle
    const cartOpenBtn = this.querySelector('[data-cart-open]');
    if (cartOpenBtn) {
      cartOpenBtn.addEventListener('click', () => {
        this._dispatchCartEvent('cart:open');
      });
    }

    // Desktop dropdown behavior (click on mobile, handled by CSS hover on desktop)
    this.menuItems.forEach((item) => {
      const link = item.querySelector('.header__menu-link');
      const submenu = item.querySelector('.header__submenu');

      if (submenu && link) {
        // Only handle click toggle on mobile/tablet
        link.addEventListener('click', (e) => {
          if (window.innerWidth < 990) {
            e.preventDefault();
            item.classList.toggle('is-open');
          }
        });
      }
    });

    // Recalculate on resize
    window.addEventListener('resize', this._onResize);
  }

  _handleScroll() {
    if (!this.header) return;
    if (window.scrollY > 50) {
      this.header.classList.add('is-scrolled');
    } else {
      this.header.classList.remove('is-scrolled');
    }
  }

  _handleEscape(e) {
    if (e.key === 'Escape') {
      if (this.mobileMenu?.classList.contains('is-open')) {
        this.closeMenu();
      }
      if (this.searchInput?.classList.contains('is-open')) {
        this._closeSearch();
      }
    }
  }

  _handleResize() {
    // Close mobile menu on resize to desktop
    if (window.innerWidth >= 990 && this.mobileMenu?.classList.contains('is-open')) {
      this.closeMenu();
    }
  }

  _toggleSearch() {
    if (!this.searchInput) return;

    if (this.searchInput.classList.contains('is-open')) {
      this._closeSearch();
    } else {
      this.searchInput.classList.add('is-open');
      this.searchInput.focus();
    }
  }

  _closeSearch() {
    if (!this.searchInput) return;
    this.searchInput.classList.remove('is-open');
    this.searchInput.value = '';
  }

  _dispatchCartEvent(eventName) {
    document.dispatchEvent(new CustomEvent(eventName));
  }

  openMenu() {
    if (this.mobileMenu) {
      this.mobileMenu.classList.add('is-open');
    }
    if (this.overlay) {
      this.overlay.classList.add('is-active');
    }
    document.body.classList.add('menu-open');

    if (this.menuToggle) {
      this.menuToggle.setAttribute('aria-expanded', 'true');
    }

    // Trap focus inside mobile menu
    if (this.mobileMenu) {
      trapFocus(this.mobileMenu);
    }
  }

  closeMenu() {
    if (this.mobileMenu) {
      this.mobileMenu.classList.remove('is-open');
    }
    if (this.overlay) {
      this.overlay.classList.remove('is-active');
    }
    document.body.classList.remove('menu-open');

    if (this.menuToggle) {
      this.menuToggle.setAttribute('aria-expanded', 'false');
    }

    // Remove focus trap
    if (this.mobileMenu) {
      removeTrapFocus(this.mobileMenu);
    }
  }
}

customElements.define('lamier-header', LamierHeader);

/* ==========================
   4. Custom Element: <lamier-cart-drawer>
   ========================== */

class LamierCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = null;
    this.overlay = null;
    this.closeBtn = null;
    this.itemsContainer = null;
    this.sectionId = this.getAttribute('data-section-id') || 'cart-drawer';
  }

  connectedCallback() {
    this.drawer = this.querySelector('[data-cart-drawer]');
    this.overlay = document.querySelector('[data-cart-overlay]') || this.querySelector('.cart-drawer-overlay');
    this.closeBtn = this.querySelector('[data-cart-close]');
    this.itemsContainer = this.querySelector('#cart-drawer-items');

    // Bind listeners
    this._onCartOpen = this.open.bind(this);
    this._onCartClose = this.close.bind(this);
    this._onEscape = this._handleEscape.bind(this);

    this._bindEvents();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:open', this._onCartOpen);
    document.removeEventListener('cart:close', this._onCartClose);
    document.removeEventListener('keydown', this._onEscape);
  }

  _bindEvents() {
    // Listen for custom events
    document.addEventListener('cart:open', this._onCartOpen);
    document.addEventListener('cart:close', this._onCartClose);

    // Also handle direct click triggers
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-open]')) {
        this.open();
      }
    });

    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key
    document.addEventListener('keydown', this._onEscape);

    // Quantity change event delegation
    if (this.drawer) {
      this.drawer.addEventListener('click', (e) => {
        // Quantity plus/minus
        const qtyBtn = e.target.closest('[data-qty-minus], [data-qty-plus]');
        if (qtyBtn) {
          const item = qtyBtn.closest('[data-cart-item]');
          if (!item) return;
          const input = item.querySelector('[data-qty-input]');
          if (!input) return;
          const delta = qtyBtn.hasAttribute('data-qty-minus') ? -1 : 1;
          const newVal = parseInt(input.value) + delta;
          if (newVal >= 0) {
            this.updateQuantity(item.dataset.key, newVal);
          }
        }

        // Remove item
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          const item = removeBtn.closest('[data-cart-item]');
          if (item) {
            this.removeItem(item.dataset.key, item);
          }
        }
      });
    }

    // Product form add-to-cart events (from product page)
    document.addEventListener('cart:add', ((e) => {
      this._onItemAdded(e.detail);
    }).bind(this));
  }

  _handleEscape(e) {
    if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
      this.close();
    }
  }

  open() {
    if (!this.drawer || !this.overlay) return;

    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-active');
    document.body.classList.add('drawer-open');

    // Trap focus inside drawer
    trapFocus(this.drawer);

    // Fetch fresh cart content
    this.getCart();
  }

  close() {
    if (!this.drawer || !this.overlay) return;

    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-active');
    document.body.classList.remove('drawer-open');

    removeTrapFocus(this.drawer);
  }

  /**
   * Fetch cart data and re-render the drawer content.
   */
  async getCart() {
    try {
      const cart = await fetchJSON(window.routes?.cart_url || '/cart.js');
      this._renderCart(cart);
    } catch (error) {
      console.error('[Lamier] Failed to fetch cart:', error);
    }
  }

  /**
   * Update item quantity via Shopify AJAX API.
   */
  async updateQuantity(key, quantity) {
    try {
      const data = await fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });

      this._renderCart(data);
      this._updateCartCount(data.item_count);
    } catch (error) {
      console.error('[Lamier] Failed to update quantity:', error);
    }
  }

  /**
   * Remove an item (set quantity to 0) with animation.
   */
  async removeItem(key, itemElement) {
    if (itemElement) {
      itemElement.classList.add('is-removing');
    }

    try {
      await fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });

      // Wait for animation to complete
      if (itemElement) {
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      this.getCart();
    } catch (error) {
      console.error('[Lamier] Failed to remove item:', error);
      if (itemElement) {
        itemElement.classList.remove('is-removing');
      }
    }
  }

  /**
   * Render cart items into the drawer.
   */
  _renderCart(cart) {
    if (!this.itemsContainer) return;

    if (cart.item_count === 0) {
      this.itemsContainer.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Your cart is empty</p>
          <a href="/collections/all" class="btn btn-primary">Continue Shopping</a>
        </div>
      `;
    } else {
      this.itemsContainer.innerHTML = cart.items.map((item) => `
        <div class="cart-item" data-cart-item="${item.key}">
          <a href="${item.url}">
            <img class="cart-item__image" src="${item.image}" alt="${item.product_title}" loading="lazy">
          </a>
          <div class="cart-item__info">
            <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
            ${item.variant_title ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
            <div class="cart-item__bottom">
              <div class="cart-item__quantity">
                <button type="button" data-qty-minus aria-label="Decrease quantity">&minus;</button>
                <input type="number" data-qty-input value="${item.quantity}" min="0" aria-label="Quantity">
                <button type="button" data-qty-plus aria-label="Increase quantity">&plus;</button>
              </div>
              <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
            </div>
            <button type="button" data-cart-remove class="cart-item__remove">Remove</button>
          </div>
        </div>
      `).join('');
    }

    // Update subtotal
    const subtotalEl = this.querySelector('.cart-drawer__subtotal span:last-child');
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price);
    }

    // Update cart count in header
    this._updateCartCount(cart.item_count);
  }

  /**
   * Update cart count badge in the header.
   */
  _updateCartCount(count) {
    const badges = document.querySelectorAll('.header__cart-count');
    badges.forEach((badge) => {
      badge.textContent = count;
      if (count > 0) {
        badge.classList.remove('is-hidden');
      } else {
        badge.classList.add('is-hidden');
      }
    });
  }

  /**
   * Handle item added event from product page.
   */
  _onItemAdded(detail) {
    // Open drawer
    this.open();
  }
}

customElements.define('lamier-cart-drawer', LamierCartDrawer);

/* ==========================
   5. Custom Element: <lamier-announcement-bar>
   ========================== */

class LamierAnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.dismissBtn = null;
  }

  connectedCallback() {
    this.dismissBtn = this.querySelector('[data-dismiss]');

    // Check if already dismissed in this session
    if (sessionStorage.getItem('lamier-announcement-dismissed') === 'true') {
      this.setAttribute('hidden', '');
      this._updateHeaderOffset();
      return;
    }

    // Bind dismiss
    if (this.dismissBtn) {
      this.dismissBtn.addEventListener('click', () => this._dismiss());
    }

    // Set initial header offset
    this._updateHeaderOffset();
  }

  _dismiss() {
    this.style.transition = 'max-height 0.3s ease, opacity 0.3s ease';
    this.style.maxHeight = `${this.offsetHeight}px`;
    // Force reflow
    this.offsetHeight; // eslint-disable-line no-unused-expressions
    this.style.maxHeight = '0';
    this.style.opacity = '0';
    this.style.overflow = 'hidden';

    setTimeout(() => {
      this.setAttribute('hidden', '');
      this._updateHeaderOffset();
    }, 300);

    sessionStorage.setItem('lamier-announcement-dismissed', 'true');
  }

  _updateHeaderOffset() {
    const header = document.querySelector('.header');
    if (!header) return;

    const height = this.hasAttribute('hidden') ? 0 : this.offsetHeight;
    document.documentElement.style.setProperty(
      '--announcement-height',
      `${height}px`
    );
  }
}

customElements.define('lamier-announcement-bar', LamierAnnouncementBar);

/* ==========================
   6. Custom Element: <lamier-quantity>
   ========================== */

class LamierQuantity extends HTMLElement {
  constructor() {
    super();
    this.input = null;
    this.minusBtn = null;
    this.plusBtn = null;
  }

  connectedCallback() {
    this.input = this.querySelector('input');
    this.minusBtn = this.querySelector('[data-qty-minus]');
    this.plusBtn = this.querySelector('[data-qty-plus]');

    if (this.minusBtn) {
      this.minusBtn.addEventListener('click', () => this._changeQty(-1));
    }
    if (this.plusBtn) {
      this.plusBtn.addEventListener('click', () => this._changeQty(1));
    }

    this.input?.addEventListener('change', () => this._validate());
  }

  _changeQty(delta) {
    const newVal = parseInt(this.input.value) + delta;
    const min = parseInt(this.input.min) || 1;
    const max = parseInt(this.input.max) || Infinity;
    if (newVal >= min && newVal <= max) {
      this.input.value = newVal;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  _validate() {
    const min = parseInt(this.input.min) || 1;
    const max = parseInt(this.input.max) || Infinity;
    let val = parseInt(this.input.value);
    if (isNaN(val) || val < min) val = min;
    if (val > max) val = max;
    this.input.value = val;
  }
}

customElements.define('lamier-quantity', LamierQuantity);

/* ==========================
   7. Product Page Interactions
   ========================== */

/**
 * Product variant selection handler.
 * Updates URL, featured image, and price when a variant is selected.
 */
function initProductVariants() {
  const variantSelects = document.querySelector('[data-variant-selects]');
  if (!variantSelects) return;

  const variantData = JSON.parse(
    document.querySelector('[data-variants-json]')?.textContent || '[]'
  );

  variantSelects.addEventListener('change', (e) => {
    const target = e.target;
    if (!target.matches('select[name="variant"], input[name="variant"]')) return;

    const selectedOptions = Array.from(
      variantSelects.querySelectorAll('select[name="variant"], input[name="variant"]:checked')
    ).map((el) => el.value);

    // Find matching variant
    const matchedVariant = variantData.find((variant) => {
      return selectedOptions.every(
        (option, index) => option === variant.options[index]
      );
    });

    if (!matchedVariant) return;

    // Update URL
    const url = new URL(window.location);
    url.searchParams.set('variant', matchedVariant.id);
    window.history.replaceState({}, '', url.toString());

    // Update price
    const priceEl = document.querySelector('[data-product-price]');
    const compareEl = document.querySelector('[data-product-compare-price]');

    if (priceEl) {
      priceEl.textContent = formatMoney(matchedVariant.price);
    }

    if (compareEl) {
      if (matchedVariant.compare_at_price > matchedVariant.price) {
        compareEl.textContent = formatMoney(matchedVariant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    // Update featured image
    if (matchedVariant.featured_media) {
      const mainImage = document.querySelector('[data-product-featured-image]');
      const thumbnail = document.querySelector(
        `[data-thumbnail-media-id="${matchedVariant.featured_media.id}"]`
      );

      if (mainImage && thumbnail) {
        mainImage.src = thumbnail.dataset.thumbnailSrc || '';
        mainImage.srcset = thumbnail.dataset.thumbnailSrcset || '';

        // Update active thumbnail
        document.querySelectorAll('.product-thumbnail').forEach((t) => {
          t.classList.remove('is-active');
        });
        thumbnail.classList.add('is-active');
      }
    }

    // Update availability
    const addToCartBtn = document.querySelector('[data-add-to-cart]');
    if (addToCartBtn) {
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = matchedVariant.id;
      }

      if (matchedVariant.available) {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = addToCartBtn.dataset.defaultText || 'Add to Cart';
      } else {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Sold Out';
      }
    }
  });
}

/**
 * Product thumbnail gallery with keyboard navigation.
 */
function initProductGallery() {
  const thumbnails = document.querySelectorAll('[data-thumbnail]');
  if (!thumbnails.length) return;

  // Event delegation on thumbnail container
  const gallery = thumbnails[0]?.closest('[data-product-gallery]');
  if (!gallery) return;

  gallery.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-thumbnail]');
    if (!thumb) return;

    thumbnails.forEach((t) => t.classList.remove('is-active'));
    thumb.classList.add('is-active');

    const mainImage = document.querySelector('[data-product-featured-image]');
    if (mainImage) {
      mainImage.style.opacity = '0';
      setTimeout(() => {
        mainImage.src = thumb.dataset.thumbnailSrc || '';
        mainImage.srcset = thumb.dataset.thumbnailSrcset || '';
        mainImage.style.opacity = '1';
      }, 150);
    }
  });

  // Keyboard navigation for thumbnails
  gallery.addEventListener('keydown', (e) => {
    const activeThumb = gallery.querySelector('.product-thumbnail.is-active') || thumbnails[0];
    const index = Array.from(thumbnails).indexOf(activeThumb);

    if (e.key === 'ArrowRight' && index < thumbnails.length - 1) {
      thumbnails[index + 1].click();
      thumbnails[index + 1].focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      thumbnails[index - 1].click();
      thumbnails[index - 1].focus();
    }
  });
}

/**
 * Accordion / Collapsible tabs on product page.
 */
function initAccordions() {
  const accordions = document.querySelectorAll('[data-accordion]');
  if (!accordions.length) return;

  accordions.forEach((accordion) => {
    const triggers = accordion.querySelectorAll('[data-accordion-trigger]');
    const content = accordion.querySelector('[data-accordion-content]');

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const isOpen = accordion.classList.contains('is-open');

        // Close all other accordions in the same group
        const group = accordion.closest('[data-accordion-group]');
        if (group) {
          group.querySelectorAll('[data-accordion].is-open').forEach((openItem) => {
            if (openItem !== accordion) {
              openItem.classList.remove('is-open');
              const openContent = openItem.querySelector('[data-accordion-content]');
              if (openContent) {
                openContent.style.maxHeight = null;
              }
            }
          });
        }

        // Toggle current
        if (isOpen) {
          accordion.classList.remove('is-open');
          if (content) {
            content.style.maxHeight = null;
          }
        } else {
          accordion.classList.add('is-open');
          if (content) {
            content.style.maxHeight = `${content.scrollHeight}px`;
          }
        }
      });
    });
  });
}

/**
 * Image zoom effect on product page.
 * Tracks mouse position for zoom effect.
 */
function initImageZoom() {
  const zoomContainer = document.querySelector('[data-image-zoom]');
  if (!zoomContainer) return;

  const image = zoomContainer.querySelector('img');
  if (!image) return;

  zoomContainer.addEventListener('mouseenter', () => {
    zoomContainer.classList.add('is-zooming');
  });

  zoomContainer.addEventListener('mouseleave', () => {
    zoomContainer.classList.remove('is-zooming');
    image.style.transformOrigin = 'center center';
  });

  zoomContainer.addEventListener('mousemove', throttle((e) => {
    const rect = zoomContainer.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    image.style.transformOrigin = `${x}% ${y}%`;
  }, 16));
}

/**
 * Add to cart form submission.
 * Posts to /cart/add.js and shows feedback.
 */
function initAddToCart() {
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;

    e.preventDefault();

    const submitBtn = form.querySelector('[data-add-to-cart]');
    if (!submitBtn || submitBtn.disabled) return;

    const originalText = submitBtn.textContent;

    // Show loading state
    submitBtn.classList.add('is-loading');
    submitBtn.disabled = true;

    try {
      const formData = new FormData(form);
      const items = formData.getAll('items[]');

      let body;
      if (items.length > 0 && !formData.get('id')) {
        body = { items: items.map((item) => JSON.parse(item)) };
      } else {
        body = {
          id: formData.get('id'),
          quantity: parseInt(formData.get('quantity')) || 1
        };
      }

      const response = await fetchJSON(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      // Success - show feedback
      submitBtn.classList.remove('is-loading');
      submitBtn.textContent = 'Added!';
      submitBtn.classList.add('is-success');

      // Dispatch custom event for cart drawer
      document.dispatchEvent(
        new CustomEvent('cart:add', { detail: response })
      );

      // Reset button after delay
      setTimeout(() => {
        submitBtn.classList.remove('is-success');
        submitBtn.textContent = originalText;
        submitBtn.disabled = false;
      }, 2000);

      // Open cart drawer
      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (error) {
      submitBtn.classList.remove('is-loading');
      submitBtn.textContent = originalText;
      submitBtn.disabled = false;

      // Show error message
      const errorEl = form.querySelector('[data-form-error]');
      if (errorEl) {
        errorEl.textContent = error.message || 'Something went wrong. Please try again.';
        errorEl.style.display = 'block';
        setTimeout(() => {
          errorEl.style.display = 'none';
        }, 5000);
      }

      console.error('[Lamier] Add to cart error:', error);
    }
  });
}

/**
 * Buy it now button - submits checkout form directly.
 */
function initBuyNow() {
  document.addEventListener('click', (e) => {
    const buyNowBtn = e.target.closest('[data-buy-now]');
    if (!buyNowBtn) return;

    e.preventDefault();

    const form = buyNowBtn.closest('form[data-product-form]');
    if (!form) return;

    // Create a temporary form that goes to checkout
    const tempForm = document.createElement('form');
    tempForm.method = 'POST';
    tempForm.action = '/cart/add';

    const formData = new FormData(form);
    const id = formData.get('id');
    const quantity = formData.get('quantity') || 1;

    // Build hidden inputs
    const idInput = document.createElement('input');
    idInput.type = 'hidden';
    idInput.name = 'id';
    idInput.value = id;
    tempForm.appendChild(idInput);

    const qtyInput = document.createElement('input');
    qtyInput.type = 'hidden';
    qtyInput.name = 'quantity';
    qtyInput.value = quantity;
    tempForm.appendChild(qtyInput);

    // Add return-to checkout
    const returnInput = document.createElement('input');
    returnInput.type = 'hidden';
    returnInput.name = 'return_to';
    returnInput.value = '/checkout';
    tempForm.appendChild(returnInput);

    document.body.appendChild(tempForm);
    tempForm.submit();
    document.body.removeChild(tempForm);
  });
}

/* ==========================
   8. Blog Page Enhancements
   ========================== */

function initBlogPage() {
  // Article card hover effects are handled by CSS.
  // Any additional JS-driven interactions can be added here.
}

/* ==========================
   9. Global Initialization
   ========================== */

document.addEventListener('DOMContentLoaded', () => {
  // Initialize scroll animations
  initScrollAnimations();

  // Initialize product page features (only if on product template)
  if (document.querySelector('[data-product-form]')) {
    initProductVariants();
    initProductGallery();
    initAccordions();
    initImageZoom();
    initAddToCart();
    initBuyNow();
  }

  // Initialize standalone accordions (e.g., FAQ pages)
  if (document.querySelector('[data-accordion]:not([data-product-form] *)')) {
    initAccordions();
  }

  // Blog page
  initBlogPage();
});

/* ==========================
   10. Global Event Listeners
   ========================== */

// Smooth scroll for anchor links
document.addEventListener('click', (e) => {
  const anchor = e.target.closest('a[href^="#"]');
  if (!anchor) return;

  const targetId = anchor.getAttribute('href');
  if (targetId === '#') return;

  const target = document.querySelector(targetId);
  if (target) {
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
});
